import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import api from '../utils/api';

export default function WheelPage() {
  const { user } = useAuth();
  const canvasRef = useRef();
  const angleRef = useRef(Math.PI / 2); // start at top
  const [prizes, setPrizes] = useState([]);
  const [spinning, setSpinning] = useState(false);
  const [spunToday, setSpunToday] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);

  // Load prizes and status
  useEffect(() => {
    const loadPrizes = api.get('/wheel/prizes').then(({ data }) => {
      if (data.prizes?.length) setPrizes(data.prizes);
    });

    if (user) {
      api.get('/wheel/status').then(({ data }) => {
        if (data.prizes?.length) setPrizes(data.prizes);
        setSpunToday(data.spunToday);
        if (data.spin) setResult(data.spin);
      });
      api.get('/wheel/history').then(({ data }) => setHistory(data.spins || []));
    }

    Promise.all([loadPrizes]).finally(() => setLoading(false));
  }, [user]);

  // Draw wheel whenever prizes load or angle changes
  useEffect(() => {
    if (prizes.length > 0) drawWheel(angleRef.current);
  }, [prizes]);

  const drawWheel = (angle) => {
    const canvas = canvasRef.current;
    if (!canvas || prizes.length === 0) return;
    const ctx = canvas.getContext('2d');
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const r = cy - 14;
    const n = prizes.length;
    const arc = (2 * Math.PI) / n;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Outer decorative ring
    ctx.beginPath();
    ctx.arc(cx, cy, r + 12, 0, 2 * Math.PI);
    ctx.fillStyle = '#1a1400';
    ctx.fill();
    ctx.strokeStyle = '#f5c400';
    ctx.lineWidth = 3;
    ctx.stroke();

    // Draw segments
    prizes.forEach((prize, i) => {
      const start = angle + i * arc;
      const end = start + arc;

      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, start, end);
      ctx.closePath();
      ctx.fillStyle = prize.color || '#f5c400';
      ctx.fill();
      ctx.strokeStyle = '#0a0900';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Label text
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(start + arc / 2);
      ctx.textAlign = 'right';
      ctx.fillStyle = '#fff';
      ctx.shadowColor = 'rgba(0,0,0,0.9)';
      ctx.shadowBlur = 5;

      const words = prize.label.split(' ');
      const fontSize = Math.max(9, Math.min(12, 120 / n));
      ctx.font = `bold ${fontSize}px Rajdhani, sans-serif`;

      if (words.length <= 2) {
        ctx.fillText(prize.label, r - 14, 4);
      } else {
        ctx.fillText(words.slice(0, Math.ceil(words.length / 2)).join(' '), r - 14, -3);
        ctx.fillText(words.slice(Math.ceil(words.length / 2)).join(' '), r - 14, 9);
      }
      ctx.restore();
    });

    // Center circle
    ctx.beginPath();
    ctx.arc(cx, cy, 20, 0, 2 * Math.PI);
    ctx.fillStyle = '#0a0900';
    ctx.fill();
    ctx.strokeStyle = '#f5c400';
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.fillStyle = '#f5c400';
    ctx.font = 'bold 11px Orbitron, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('APEX', cx, cy);

    // ── Fixed pointer at TOP CENTER (12 o'clock) ──
    // This is outside the rotating wheel canvas, drawn last so it stays fixed
    ctx.beginPath();
    ctx.moveTo(cx, 4);           // tip of arrow at top
    ctx.lineTo(cx - 10, 24);
    ctx.lineTo(cx + 10, 24);
    ctx.closePath();
    ctx.fillStyle = '#ef4444';
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  };

  const getWinningPrize = (finalAngle) => {
    if (prizes.length === 0) return null;
    const arc = (2 * Math.PI) / prizes.length;
    // Pointer is at top (angle = -PI/2 from 0)
    // Normalize the angle so we know which segment is under the pointer
    const normalized = (((-finalAngle + Math.PI / 2) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    const idx = Math.floor(normalized / arc) % prizes.length;
    return prizes[idx];
  };

  const handleSpin = async () => {
    if (spinning || spunToday || !user || prizes.length === 0) return;
    setSpinning(true); setResult(null);

    // Spin 5-8 full rotations + random offset
    const fullRotations = (5 + Math.floor(Math.random() * 3)) * 2 * Math.PI;
    const randomOffset = Math.random() * 2 * Math.PI;
    const totalSpin = fullRotations + randomOffset;
    const finalAngle = angleRef.current - totalSpin; // subtract because we spin clockwise

    const duration = 4500;
    const startTime = performance.now();
    const startAngle = angleRef.current;

    const animate = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const current = startAngle + (finalAngle - startAngle) * eased;
      angleRef.current = current;
      drawWheel(current);

      if (t < 1) {
        requestAnimationFrame(animate);
      } else {
        // Call API to get official result
        api.post('/wheel/spin').then(({ data }) => {
          setResult(data.spin);
          setSpunToday(true);
          setHistory(prev => [data.spin, ...prev.slice(0, 29)]);
        }).catch(err => {
          alert(err.response?.data?.message || 'Error spinning');
        }).finally(() => setSpinning(false));
      }
    };

    requestAnimationFrame(animate);
  };

  const prizeTypeColor = (type) => type === 'freeplay' ? '#f09433' : type === 'nothing' ? '#666' : '#f5c400';

  return (
    <div style={{ paddingTop: 70 }}>
      <div className="page-header">
        <h1>🎡 WHEEL OF FORTUNE</h1>
        <p>Spin daily — FREE! Win Fun Credits & Freeplay bonuses</p>
      </div>

      <div className="container" style={{ paddingBottom: 80 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 32, alignItems: 'start', maxWidth: 880, margin: '0 auto' }}>

          {/* Wheel column */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <canvas
                ref={canvasRef}
                width={340}
                height={340}
                style={{ display: 'block', filter: 'drop-shadow(0 0 30px rgba(245,196,0,0.3))' }}
              />
            </div>

            {/* Result */}
            {result && (
              <div style={{ textAlign: 'center', padding: '16px 32px', background: result.prizeType === 'nothing' ? 'rgba(100,100,100,0.1)' : 'rgba(245,196,0,0.1)', border: `2px solid ${prizeTypeColor(result.prizeType)}`, borderRadius: 16, width: '100%', maxWidth: 340, animation: 'fadeIn 0.4s ease' }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>
                  {result.prizeType === 'nothing' ? '😔' : result.prizeType === 'freeplay' ? '🎮' : '🎉'}
                </div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: prizeTypeColor(result.prizeType), fontWeight: 900 }}>
                  {result.prize}
                </div>
                {result.prizeType === 'credits' && result.prizeValue > 0 && (
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 8 }}>
                    ✅ Added to your <Link to="/games" style={{ color: '#f5c400', fontWeight: 700 }}>Mini Games balance</Link>!
                  </p>
                )}
                {result.prizeType === 'freeplay' && (
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 8 }}>
                    Contact support via <Link to="/chat" style={{ color: '#f5c400', fontWeight: 700 }}>Live Chat</Link> to claim!
                  </p>
                )}
              </div>
            )}

            {/* Spin button / status */}
            {!user ? (
              <Link to="/login" className="btn btn-primary" style={{ fontSize: 18, padding: '14px 48px' }}>Login to Spin</Link>
            ) : loading ? (
              <div className="spinner" />
            ) : spunToday ? (
              <div style={{ textAlign: 'center', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 12, padding: '14px 28px' }}>
                <div style={{ color: '#10b981', fontWeight: 700, fontSize: 15 }}>✅ Already spun today!</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>Come back tomorrow 🌟</div>
              </div>
            ) : (
              <button
                onClick={handleSpin}
                disabled={spinning || prizes.length === 0}
                style={{ padding: '16px 56px', background: spinning ? '#333' : 'linear-gradient(135deg,#f5c400,#c9a000)', border: 'none', borderRadius: 14, fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 900, color: '#0a0900', cursor: spinning ? 'not-allowed' : 'pointer', boxShadow: spinning ? 'none' : '0 6px 24px rgba(245,196,0,0.4)', transition: 'all 0.2s' }}>
                {spinning ? '⟳ SPINNING...' : '🎡 SPIN NOW!'}
              </button>
            )}

            <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
              🆓 One free spin per day • No deposit required
            </p>
          </div>

          {/* Right panel */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Prize list */}
            <div className="card">
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: '#f5c400', marginBottom: 14 }}>🏆 PRIZES</h3>
              {prizes.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading prizes...</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {prizes.map((p, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
                      <div style={{ width: 14, height: 14, borderRadius: '50%', background: p.color, flexShrink: 0, border: '1px solid rgba(255,255,255,0.2)' }} />
                      <span style={{ fontSize: 13, color: 'var(--text-secondary)', flex: 1 }}>{p.label}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-card)', padding: '2px 7px', borderRadius: 8 }}>
                        {p.weight}% chance
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Fun Credits info */}
            <div className="card" style={{ background: 'rgba(245,196,0,0.04)', border: '1px solid rgba(245,196,0,0.2)' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 13, color: '#f5c400', marginBottom: 10 }}>ℹ️ HOW IT WORKS</h3>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 7 }}>
                {[
                  '🎡 1 free spin every 24 hours',
                  '🎰 Credits auto-added to Mini Games balance',
                  '💎 Collect $100 daily credits separately',
                  '🎮 Freeplay prizes — claim via Live Chat',
                  '💰 Redeem $1,000+ FC for real freeplay',
                ].map((t, i) => (
                  <li key={i} style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t}</li>
                ))}
              </ul>
            </div>

            {/* Spin history */}
            {user && history.length > 0 && (
              <div className="card">
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, color: '#f5c400', marginBottom: 12 }}>MY HISTORY</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
                  {history.map((s, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', background: 'var(--bg-secondary)', borderRadius: 7, fontSize: 12 }}>
                      <span style={{ color: prizeTypeColor(s.prizeType), fontWeight: 600 }}>{s.prize}</span>
                      <span style={{ color: 'var(--text-muted)' }}>{s.date}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
